import axios from 'axios';
import { getAuth } from 'firebase/auth';
import {
  clearPendingSignupRolePreference,
  getPendingSignupRolePreference,
} from 'domains/auth/utils/registrationRole';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api'
});

// Helper function for auto-recovery when user not in database
async function autoRecoverUser(firebaseUid, error) {
  if (error.response?.data?.code === 'USER_NOT_IN_DATABASE') {
    if (typeof window !== 'undefined' && window.location.pathname.includes('/auth/')) {
      console.log('Skipping auto-recovery in api.js because user is in auth flow.');
      return false;
    }

    console.log('User not in database - attempting auto-recovery');

    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (currentUser) {
      const token = await currentUser.getIdToken();
      const rolePreference =
        getPendingSignupRolePreference(currentUser.email) || 'client';

      await API.post('/users', {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName || currentUser.email,
        rolePreference,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      clearPendingSignupRolePreference();
      console.log('Auto-recovery successful - user created in database');
      return true;
    }
  }
  return false;
}

API.interceptors.request.use(
  async (req) => {
    // Skip auth for requests with X-Skip-Auth header (e.g., public health endpoint)
    if (req.headers['X-Skip-Auth']) {
      delete req.headers['X-Skip-Auth'];
      return req;
    }

    const auth = getAuth();

    // Wait for Firebase to initialize if currentUser is null
    if (!auth.currentUser) {
      await new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged(() => {
          unsubscribe();
          resolve();
        });
      });
    }

    const user =
      auth.currentUser ||
      (await new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((u) => {
          unsubscribe();
          resolve(u);
        });
      }));
    if (user) {
      // Always fetch a fresh token
      const token = await user.getIdToken();
      req.headers.Authorization = `Bearer ${token}`;
    }

    const locale =
      (typeof window !== 'undefined' &&
        window.localStorage.getItem('logiflow.language')) ||
      (typeof document !== 'undefined' && document.documentElement?.lang) ||
      'en';
    req.headers['Accept-Language'] = locale;
    req.headers['X-Locale'] = locale;

    return req;
  },
  (error) => Promise.reject(error)
);

export const getUserByFirebaseUid = async (firebaseUid) => {
  try {
    const response = await API.get(`/users/${firebaseUid}`);
    console.log(response);
    return response.data;
  } catch (error) {
    console.error('Error fetching user by Firebase UID:', error);

    // AUTO-RECOVERY: Try to create user if they don't exist in database
    const recovered = await autoRecoverUser(firebaseUid, error);
    if (recovered) {
      // Retry after successful recovery
      const retryResponse = await API.get(`/users/${firebaseUid}`);
      return retryResponse.data;
    }

    throw error;
  }
};

export default API;
